import { BaseService } from "../baseService";
import { FileAPIService } from "../fileAPIService";
import { PurchaseOrderAPIService } from "./purchaseOrderAPIService";
import { incrementDateByDays, isDateInTheFuture, isEqualFiles } from "src/helpers";
import { sanitiseParams } from "src/utils/sanitiser";
import { ValidationError } from "src/errors";
import { POCompletionStatus, PurchaseOrder, POInvoiceStatus } from "src/types/database/purchaseOrder";
import {
  GetPurchaseOrdersQueryParams,
  GetPurchaseOrdersByBarcodeQueryParams,
  GetPurchaseOrdersForEuRelabelQueryParams,
  GetPurchaseOrdersForRelabelCaseBuilderQueryParams,
  CreatedAtDateTimeRange,
  S3SignedUrl,
  S3Operation,
  PaginatedPurchaseOrders
} from "src/types";
import {
  PatchPurchaseOrderRequestBody,
  PostPurchaseOrderConfirmationRequestBody,
  PatchPurchaseOrderInvoiceRequestBody,
  PatchPurchaseOrderComplianceRequestBody,
  PatchPurchaseOrderShipmentDetailsRequestBody,
  PostPurchaseOrderEmailRequestBody,
  PostPurchaseOrdersExportsRequestBody,
  PostPurchaseOrderPDFRequestBody,
  PostRerunParcelHeroRequestBody,
  PostChildPurchaseOrderRequestBody,
  PatchScheduledPOApprovalRequestBody
} from "src/types/api";

export const POST_PO_DELIVERY_INVOICE_UPDATE_WINDOW_DAYS = 30;

export const POST_PO_DELIVERY_SHIPMENT_DETAILS_UPDATE_WINDOW_DAYS = 30;

class PurchaseOrderService extends BaseService<PurchaseOrderAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: PurchaseOrderAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  async importPurchaseOrders(importFile: File) {
    const { fileName: importFileId, signedUrl } = await this.apiService.postPurchaseOrdersImportUrl();

    await this.fileAPIService.putFile(importFile, signedUrl);

    const response = await this.apiService.postPurchaseOrdersImports(importFileId);

    return response;
  }

  async createManualPurchaseOrder(
    vendorName: string,
    products: Array<{ productId: string; ukQuantity: number; euQuantity: number; extraInfo?: string }>,
    isInternalHealfOrder: boolean = false
  ) {
    const response = await this.apiService.postPurchaseOrdersManual({
      vendorName,
      products,
      isInternalHealfOrder
    });

    return response;
  }

  @sanitiseParams()
  async getPurchaseOrderIds(
    completionStatus: POCompletionStatus | POCompletionStatus[],
    createdAtDateTimeRange?: CreatedAtDateTimeRange
  ): Promise<string[]> {
    const purchaseOrderIds = await this.apiService.getPurchaseOrderIds({ completionStatus, ...createdAtDateTimeRange });

    return purchaseOrderIds;
  }

  @sanitiseParams()
  async createPurchaseOrderPDF(purchaseOrderId: string, requestBody: PostPurchaseOrderPDFRequestBody) {
    const updatedPurchaseOrder = await this.apiService.postPurchaseOrderPDF(purchaseOrderId, requestBody);

    return updatedPurchaseOrder;
  }

  @sanitiseParams()
  async getPurchaseOrderPDF(purchaseOrderId: string, fileType: "PDF" | "CSV" = "PDF") {
    const { signedUrl, fileName } = await this.apiService.postPurchaseOrderPDFUrl(purchaseOrderId, fileType);

    const fileBlob = await this.fileAPIService.getFile(signedUrl);

    return new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });
  }

  static hasPurchaseOrderPDFBeenSent = (purchaseOrder: PurchaseOrder) =>
    purchaseOrder.confirmationStatus === "Confirmed" || (!purchaseOrder.portalAccess && purchaseOrder.sentAt);

  static isCommercialInvoiceAvailable = (purchaseOrder: PurchaseOrder) =>
    purchaseOrder.confirmationStatus === "Confirmed";

  @sanitiseParams()
  async getPurchaseOrderCommercialInvoice(purchaseOrderId: string, fileType: "PDF" | "CSV" = "PDF"): Promise<File> {
    const { signedUrl } = await this.apiService.postPurchaseOrderCommercialInvoiceUrl(purchaseOrderId, fileType);

    const fileBlob = await this.fileAPIService.getFile(signedUrl);

    const extension = fileType === "CSV" ? "csv" : "pdf";

    return new File([fileBlob], `commercial-invoice-${purchaseOrderId}.${extension}`, {
      type: fileBlob.type,
      lastModified: new Date().getTime()
    });
  }

  @sanitiseParams()
  async sendPurchaseOrder(purchaseOrderId: string, requestBody: PostPurchaseOrderEmailRequestBody) {
    const updatedPurchaseOrder = await this.apiService.postPurchaseOrderEmail(purchaseOrderId, requestBody);

    return updatedPurchaseOrder;
  }

  @sanitiseParams()
  async updatePurchaseOrderConfirmation(
    purchaseOrderId: string,
    requestBody: PostPurchaseOrderConfirmationRequestBody
  ) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update confirmation for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update confirmation for internal Healf orders", "purchaseOrderId");
    }

    const { estShipDate, estDeliveryDate, estCollectionDate, products } = requestBody;

    if (estShipDate && new Date(estShipDate) < new Date()) {
      throw new ValidationError("Estimated ship date must be in the future", "estShipDate");
    }

    if (estDeliveryDate && new Date(estDeliveryDate) < new Date()) {
      throw new ValidationError("Estimated delivery date must be in the future", "estDeliveryDate");
    }

    if (estShipDate && estDeliveryDate && new Date(estShipDate) > new Date(estDeliveryDate)) {
      throw new ValidationError("Estimated ship date cannot be after the estimated delivery date", "estShipDate");
    }

    if (estShipDate && new Date() > incrementDateByDays(new Date(estShipDate), 30)) {
      throw new ValidationError("Estimated ship date cannot be over a month in the future", "estShipDate");
    }

    if (estCollectionDate && !isDateInTheFuture(estCollectionDate)) {
      throw new ValidationError("Estimated collection date must be in the future", "estCollectionDate");
    }

    if (products?.some((product) => product.estRestockDate && !isDateInTheFuture(product.estRestockDate))) {
      throw new ValidationError("Estimated restock dates must be in the future", "productConfirmations");
    }

    // Note: Frontend validation handles < 50% fulfillment requirement
    // This is a fallback check for out-of-stock products
    if (products?.some((product) => product.confirmedQty === 0 && !product.estRestockDate && !product.isDiscontinued)) {
      throw new ValidationError(
        "Restock date is mandatory for out-of-stock products unless marked as discontinued",
        "productConfirmations"
      );
    }

    return await this.apiService.postPurchaseOrderConfirmation(purchaseOrderId, requestBody);
  }

  @sanitiseParams()
  async approvePurchaseOrder(purchaseOrderId: string, createBackorderPO: boolean = false) {
    return await this.apiService.postPurchaseOrderApproval(purchaseOrderId, createBackorderPO);
  }

  @sanitiseParams()
  async cancelPurchaseOrder(purchaseOrderId: string, cancellationReason: string) {
    const updatedPurchaseOrder = await this.apiService.postPurchaseOrderCancellation(purchaseOrderId, {
      cancellationReason
    });

    return updatedPurchaseOrder;
  }

  static isPOInvoiceLocked = (purchaseOrder: PurchaseOrder) =>
    PurchaseOrderService.isChildPurchaseOrder(purchaseOrder) ||
    PurchaseOrderService.isInternalHealfOrder(purchaseOrder) ||
    (purchaseOrder.actualDeliveryDate &&
      new Date() >
      incrementDateByDays(new Date(purchaseOrder.actualDeliveryDate), POST_PO_DELIVERY_INVOICE_UPDATE_WINDOW_DAYS));

  @sanitiseParams()
  async getPurchaseOrderInvoices(purchaseOrderId: string): Promise<File[]> {
    const invoiceFiles: File[] = [];

    const signedGETUrls = await this.apiService.postPurchaseOrderInvoiceUrls(purchaseOrderId, { operation: "GET" });

    for (const { signedUrl, fileName } of signedGETUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const invoiceFile = new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });

      invoiceFiles.push(invoiceFile);
    }

    return invoiceFiles;
  }

  @sanitiseParams()
  async getPurchaseOrderComplianceDocuments(purchaseOrderId: string): Promise<File[]> {
    const complianceFiles: File[] = [];

    const signedGETUrls = await this.apiService.postPurchaseOrderComplianceUrls(purchaseOrderId, { operation: "GET" });

    for (const { signedUrl, fileName } of signedGETUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const complianceFile = new File([fileBlob], fileName, {
        type: fileBlob.type,
        lastModified: new Date().getTime()
      });

      complianceFiles.push(complianceFile);
    }

    return complianceFiles;
  }

  private async handleFileUpdates(
    updatedFiles: File[],
    initialFiles: File[],
    getSignedUrls: (fileNames: string[], operation: Exclude<S3Operation, "GET">) => Promise<S3SignedUrl[]>
  ): Promise<void> {
    const updatedFileNames = updatedFiles.map(({ name }) => name);
    const initialFileNames = initialFiles.map(({ name }) => name);

    const filesToDeleteNames = initialFileNames.filter((fileName) => !updatedFileNames.includes(fileName));

    if (filesToDeleteNames.length) {
      const signedDeleteUrls = await getSignedUrls(filesToDeleteNames, "DELETE");

      for (const { signedUrl } of signedDeleteUrls) {
        await this.fileAPIService.deleteFile(signedUrl);
      }
    }

    const filesToUpload = updatedFiles.filter((file) => !initialFileNames.includes(file.name));

    if (filesToUpload.length) {
      const filesToUploadNames = filesToUpload.map(({ name }) => name);
      const signedPutUrls = await getSignedUrls(filesToUploadNames, "PUT");

      for (const { signedUrl, fileName } of signedPutUrls) {
        const file = filesToUpload.find((file) => file.name === fileName);
        if (file) {
          await this.fileAPIService.putFile(file, signedUrl);
        }
      }
    }
  }

  @sanitiseParams()
  async updatePurchaseOrderInvoice(
    purchaseOrderId: string,
    updatedInvoiceFiles: File[],
    initialInvoiceFiles: File[] = [],
    requestBody: PatchPurchaseOrderInvoiceRequestBody,
    isProForma: boolean = false
  ) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update invoice for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update invoice for internal Healf orders", "purchaseOrderId");
    }

    if (!isProForma) {
      if (requestBody.invoiceDueDate && !isDateInTheFuture(requestBody.invoiceDueDate)) {
        throw new ValidationError("Invoice due date must be in the future", "invoiceDueDate");
      }
    }

    await this.handleFileUpdates(
      updatedInvoiceFiles,
      initialInvoiceFiles,
      async (fileNames, operation) =>
        await this.apiService.postPurchaseOrderInvoiceUrls(purchaseOrderId, { operation, fileNames })
    );

    return this.apiService.patchPurchaseOrderInvoice(purchaseOrderId, requestBody);
  }

  @sanitiseParams()
  async updatePurchaseOrderInvoiceStatus(purchaseOrderId: string, invoiceStatus: POInvoiceStatus) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update invoice status for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update invoice status for internal Healf orders", "purchaseOrderId");
    }

    return this.apiService.patchPurchaseOrderInvoiceStatus(purchaseOrderId, { invoiceStatus });
  }

  @sanitiseParams()
  async updatePurchaseOrderPaymentTerms(purchaseOrderId: string, paymentTerms: string | null) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update payment terms for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update payment terms for internal Healf orders", "purchaseOrderId");
    }

    return this.apiService.patchPurchaseOrderPaymentTerms(purchaseOrderId, { paymentTerms });
  }

  @sanitiseParams()
  async updatePurchaseOrderCompliance(
    purchaseOrderId: string,
    updatedComplianceFiles: File[],
    initialComplianceFiles: File[] = [],
    requestBody: PatchPurchaseOrderComplianceRequestBody
  ) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update compliance for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update compliance for internal Healf orders", "purchaseOrderId");
    }

    await this.handleFileUpdates(
      updatedComplianceFiles,
      initialComplianceFiles,
      async (fileNames, operation) =>
        await this.apiService.postPurchaseOrderComplianceUrls(purchaseOrderId, { operation, fileNames })
    );

    const documentsUpdated = !isEqualFiles(initialComplianceFiles, updatedComplianceFiles);

    return this.apiService.patchPurchaseOrderCompliance(purchaseOrderId, {
      ...requestBody,
      documentsUpdated
    });
  }

  @sanitiseParams()
  async getPurchaseOrderPackingLists(purchaseOrderId: string): Promise<File[]> {
    const packingListFiles: File[] = [];

    const signedGETUrls = await this.apiService.postPurchaseOrderPackingListUrls(purchaseOrderId, { operation: "GET" });

    for (const { signedUrl, fileName } of signedGETUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const packingListFile = new File([fileBlob], fileName, {
        type: fileBlob.type,
        lastModified: new Date().getTime()
      });

      packingListFiles.push(packingListFile);
    }

    return packingListFiles;
  }

  // TODO: temp re-add code later - once GXO fix erroneous check-ins
  static isPOShipmentDeliveryDetailsLocked = (purchaseOrder: PurchaseOrder) =>
    purchaseOrder.completionStatus !== "In Progress"; /* && !purchaseOrder.checkInTimestamps?.length; */

  static isPOShipmentDetailsLocked = (purchaseOrder: PurchaseOrder) =>
    PurchaseOrderService.isChildPurchaseOrder(purchaseOrder) ||
    PurchaseOrderService.isInternalHealfOrder(purchaseOrder) ||
    (purchaseOrder.actualDeliveryDate &&
      new Date() >
      incrementDateByDays(
        new Date(purchaseOrder.actualDeliveryDate),
        POST_PO_DELIVERY_SHIPMENT_DETAILS_UPDATE_WINDOW_DAYS
      ));

  static isPOParcelHeroEligible = (purchaseOrder: PurchaseOrder) =>
    purchaseOrder.collectionOrder &&
    purchaseOrder.confirmationStatus === "Confirmed" &&
    purchaseOrder.shipmentUnits &&
    purchaseOrder.shipmentUnits.length > 0;

  @sanitiseParams()
  async updatePurchaseOrderShipmentDetails(
    purchaseOrderId: string,
    poUpdateShipmentDetailsRequestBody: PatchPurchaseOrderShipmentDetailsRequestBody,
    updatedPackingListFiles: File[],
    initialPackingListFiles: File[] = []
  ) {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update shipment details for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot update shipment details for internal Healf orders", "purchaseOrderId");
    }

    await this.handleFileUpdates(
      updatedPackingListFiles,
      initialPackingListFiles,
      async (fileNames, operation) =>
        await this.apiService.postPurchaseOrderPackingListUrls(purchaseOrderId, { operation, fileNames })
    );

    return this.apiService.patchPurchaseOrderShipmentDetails(purchaseOrderId, poUpdateShipmentDetailsRequestBody);
  }

  @sanitiseParams()
  async getPurchaseOrderByAsn(purchaseOrderAsn: string, completionStatus?: POCompletionStatus[] | POCompletionStatus) {
    const purchaseOrder = await this.apiService.getPurchaseOrderByAsn(purchaseOrderAsn, { completionStatus });

    return purchaseOrder;
  }

  @sanitiseParams()
  async getPurchaseOrderById(purchaseOrderId: string, completionStatus?: POCompletionStatus[] | POCompletionStatus) {
    const purchaseOrder = await this.apiService.getPurchaseOrderById(purchaseOrderId, { completionStatus });

    return purchaseOrder;
  }

  @sanitiseParams()
  async getPurchaseOrders(queryParams: GetPurchaseOrdersQueryParams) {
    const paginatedPurchaseOrders = await this.apiService.getPurchaseOrders(queryParams);

    return paginatedPurchaseOrders;
  }

  @sanitiseParams()
  async getPurchaseOrdersByBarcode(queryParams: GetPurchaseOrdersByBarcodeQueryParams) {
    const paginatedPurchaseOrders = await this.apiService.getPurchaseOrdersByBarcode(queryParams);

    return paginatedPurchaseOrders;
  }

  @sanitiseParams()
  async getPurchaseOrdersForEuRelabel(queryParams: GetPurchaseOrdersForEuRelabelQueryParams) {
    const paginatedPurchaseOrders = await this.apiService.getPurchaseOrdersForEuRelabel(queryParams);

    return paginatedPurchaseOrders;
  }

  @sanitiseParams()
  async getPurchaseOrdersForRelabelCaseBuilder(queryParams: GetPurchaseOrdersForRelabelCaseBuilderQueryParams) {
    const paginatedPurchaseOrders = await this.apiService.getPurchaseOrdersForRelabelCaseBuilder(queryParams);

    return paginatedPurchaseOrders;
  }

  @sanitiseParams()
  async updatePurchaseOrderOpsNote(id: string, opsNote: string | null) {
    const updatedPurchaseOrder = await this.apiService.patchPurchaseOrderOpsNote(id, { opsNote });

    return updatedPurchaseOrder;
  }

  static isChildPurchaseOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    if (typeof purchaseOrder !== "string" && purchaseOrder.isChildPO !== undefined) {
      return purchaseOrder.isChildPO;
    }
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return id.includes("-C-");
  };

  static isInternalHealfOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    if (typeof purchaseOrder !== "string" && purchaseOrder.isInternalPO !== undefined) {
      return purchaseOrder.isInternalPO;
    }
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return id.includes("-H-");
  };

  static isBackorderPurchaseOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    if (typeof purchaseOrder !== "string" && purchaseOrder.isBackorderPO !== undefined) {
      return purchaseOrder.isBackorderPO;
    }
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return id.includes("-B-");
  };

  static isActiveAntsPurchaseOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return id.startsWith("AA-");
  };

  static isOriginalOrBackorderPurchaseOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    if (typeof purchaseOrder !== "string") {
      if (purchaseOrder.isChildPO !== undefined || purchaseOrder.isInternalPO !== undefined) {
        return !purchaseOrder.isChildPO && !purchaseOrder.isInternalPO;
      }
    }
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return !id.includes("-C-") && !id.includes("-H-");
  };

  static isOriginalPurchaseOrder = (purchaseOrder: PurchaseOrder | string): boolean => {
    if (typeof purchaseOrder !== "string") {
      if (
        purchaseOrder.isBackorderPO !== undefined ||
        purchaseOrder.isChildPO !== undefined ||
        purchaseOrder.isInternalPO !== undefined
      ) {
        return !purchaseOrder.isBackorderPO && !purchaseOrder.isChildPO && !purchaseOrder.isInternalPO;
      }
    }
    const id = typeof purchaseOrder === "string" ? purchaseOrder : purchaseOrder.id;
    return !id.includes("-B-") && !id.includes("-C-") && !id.includes("-H-");
  };

  @sanitiseParams()
  async getVendorBackorders(vendorId: string, limit?: number, pageToken?: string): Promise<PaginatedPurchaseOrders> {
    return await this.apiService.getVendorBackorders(vendorId, { limit, pageToken });
  }

  @sanitiseParams()
  async updateBackorderScheduledDate(purchaseOrderId: string, scheduledDate: string): Promise<PurchaseOrder> {
    if (!isDateInTheFuture(scheduledDate)) {
      throw new ValidationError("Scheduled date must be in the future", "scheduledDate");
    }

    return await this.apiService.patchBackorderScheduledDate(purchaseOrderId, scheduledDate);
  }

  @sanitiseParams()
  async updateScheduledPOApproval(
    purchaseOrderId: string,
    requestBody: PatchScheduledPOApprovalRequestBody
  ): Promise<PurchaseOrder> {
    return await this.apiService.patchScheduledPOApproval(purchaseOrderId, requestBody);
  }

  static isPOAmendLocked = (purchaseOrder: PurchaseOrder) =>
    PurchaseOrderService.isChildPurchaseOrder(purchaseOrder) ||
    PurchaseOrderService.isInternalHealfOrder(purchaseOrder) ||
    purchaseOrder.completionStatus === "Cancelled" ||
    purchaseOrder.completionStatus === "Complete" ||
    purchaseOrder.checkInTimestamps?.length ||
    (purchaseOrder.id.startsWith("AA-") && purchaseOrder.confirmationStatus === "Confirmed");

  static isActionRequiredAfterPOAmend = (purchaseOrder: PurchaseOrder) =>
    purchaseOrder.completionStatus === "In Progress" &&
    purchaseOrder.confirmationStatus !== "TBC" &&
    purchaseOrder.confirmationStatus !== "Overdue";

  static isChildPOAmendable = (purchaseOrder: PurchaseOrder) =>
    PurchaseOrderService.isChildPurchaseOrder(purchaseOrder) &&
    !!purchaseOrder.gxoAmendmentState &&
    purchaseOrder.completionStatus === "In Progress" &&
    !purchaseOrder.checkInTimestamps?.length &&
    !purchaseOrder.actualDeliveryDate;

  @sanitiseParams()
  async amendPurchaseOrder(purchaseOrderId: string, requestBody: PatchPurchaseOrderRequestBody) {
    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot amend internal Healf orders", "purchaseOrderId");
    }

    const { estDeliveryDate, products } = requestBody;

    if (estDeliveryDate && new Date(estDeliveryDate) < new Date()) {
      throw new ValidationError("Estimated delivery date must be after current date", "estDeliveryDate");
    }

    if (products && new Set(products.map((product) => product.id)).size !== products.length) {
      throw new ValidationError("Products cannot have duplicate IDs", "productAmendments");
    }

    if (products && products.every((product) => product.quantity === 0)) {
      throw new ValidationError("Purchase order cannot have 0 products", "productAmendments");
    }

    const updatedPurchaseOrder = await this.apiService.patchPurchaseOrder(purchaseOrderId, requestBody);

    return updatedPurchaseOrder;
  }

  @sanitiseParams()
  async createChildPurchaseOrder(parentPurchaseOrderId: string, requestBody: PostChildPurchaseOrderRequestBody) {
    if (PurchaseOrderService.isActiveAntsPurchaseOrder(parentPurchaseOrderId)) {
      throw new ValidationError(
        "Cannot create child purchase orders for Active Ants purchase orders",
        "purchaseOrderId"
      );
    }

    const { estDeliveryDate, products } = requestBody;

    const deliveryDate = new Date(estDeliveryDate);
    const today = new Date();
    deliveryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (deliveryDate < today) {
      throw new ValidationError("Estimated delivery date cannot be in the past", "estDeliveryDate");
    }

    if (products.length === 0) {
      throw new ValidationError("At least one product is required", "products");
    }

    if (products.some((product) => !product.id)) {
      throw new ValidationError("All products must have an id", "products");
    }

    if (products.every((product) => (product.ukQuantity ?? 0) + (product.euQuantity ?? 0) === 0)) {
      throw new ValidationError("At least one product must have a UK or EU quantity greater than zero", "products");
    }

    const childPO = await this.apiService.postChildPurchaseOrder(parentPurchaseOrderId, requestBody);

    return childPO;
  }

  @sanitiseParams()
  async confirmChildPurchaseOrder(childPurchaseOrderId: string): Promise<PurchaseOrder> {
    if (!PurchaseOrderService.isChildPurchaseOrder(childPurchaseOrderId)) {
      throw new ValidationError("Purchase order is not a child purchase order", "purchaseOrderId");
    }

    return await this.apiService.postChildPurchaseOrderConfirmation(childPurchaseOrderId);
  }

  @sanitiseParams()
  async deletePurchaseOrder(purchaseOrderId: string) {
    return await this.apiService.deletePurchaseOrder(purchaseOrderId);
  }

  @sanitiseParams()
  async purchaseOrdersExports(requestBody: PostPurchaseOrdersExportsRequestBody): Promise<File> {
    const { signedUrl, fileName } = await this.apiService.postPurchaseOrdersExports(requestBody);

    const fileBlob = await this.fileAPIService.getFile(signedUrl);

    return new File([fileBlob], fileName, { type: fileBlob.type, lastModified: new Date().getTime() });
  }

  @sanitiseParams()
  async rerunParcelHero(purchaseOrderId: string, requestBody?: PostRerunParcelHeroRequestBody): Promise<PurchaseOrder> {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot run Parcel Hero for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot run Parcel Hero for internal Healf orders", "purchaseOrderId");
    }
    return await this.apiService.postRerunParcelHero(purchaseOrderId, requestBody);
  }

  @sanitiseParams()
  async getParcelHeroShipmentDetails(purchaseOrderId: string): Promise<PurchaseOrder> {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot access Parcel Hero details for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot access Parcel Hero details for internal Healf orders", "purchaseOrderId");
    }
    return await this.apiService.getParcelHeroShipmentDetails(purchaseOrderId);
  }

  @sanitiseParams()
  async cancelParcelHeroShipment(purchaseOrderId: string): Promise<PurchaseOrder> {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot cancel Parcel Hero shipment for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot cancel Parcel Hero shipment for internal Healf orders", "purchaseOrderId");
    }
    return await this.apiService.postCancelParcelHeroShipment(purchaseOrderId);
  }

  @sanitiseParams()
  async rescheduleParcelHeroShipment(purchaseOrderId: string, collectionDate: string): Promise<PurchaseOrder> {
    if (PurchaseOrderService.isChildPurchaseOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot reschedule Parcel Hero shipment for child purchase orders", "purchaseOrderId");
    }

    if (PurchaseOrderService.isInternalHealfOrder(purchaseOrderId)) {
      throw new ValidationError("Cannot reschedule Parcel Hero shipment for internal Healf orders", "purchaseOrderId");
    }
    return await this.apiService.putRescheduleParcelHeroShipment(purchaseOrderId, collectionDate);
  }

  @sanitiseParams()
  async resolveGXOError(
    purchaseOrderId: string,
    requestBody: {
      products: { productId: string; barcode: string }[];
      estimatedDeliveryDate?: string;
    }
  ) {
    return await this.apiService.resolveGXOError(purchaseOrderId, requestBody);
  }
}

export { PurchaseOrderService };
